import moment from "moment";
import { sendMessageToParent } from "./common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import type { AppDispatch } from "src/redux/store";
import { getDailyLogsRequest } from "src/redux/slices/cgmSlice";
import type {
  PocGlucoseJson,
  PocGlucoseJsonForApiLog,
} from "src/types/cgm.types";
import type { CgmDisplayStatus } from "src/constants/cgm";
import {
  CGM_DISPLAY_STATUS_CONFIG,
  CGM_SENSOR_CYCLE_DAYS,
} from "src/constants/cgm";
import type { CgmState } from "src/types/cgm.types";

/**
 * Request CGM status from RN (native SDK).
 * Sends CGM_CHECK_STATUS and CGM_RECONNECT so RN will check device bind state,
 * reconnect if needed, and post CGM_STATUS_UPDATE / CGM_SENSOR_STATE back to web.
 * Use when user opens a view that shows CGM status (e.g. device list, insights).
 */
export function requestCgmStatus(): void {
  sendMessageToParent(POST_MESSAGE_EVENTS.CGM_CHECK_STATUS, {});
  sendMessageToParent(POST_MESSAGE_EVENTS.CGM_RECONNECT, {});
}

/**
 * Fetch CGM status from RN and daily logs from API.
 * Same flow as ContinuousGlucoseMonitor and InsightComponentGlucose.
 * Use when CGM device card is shown (first time or returning to bottom sheet).
 */
export function fetchCgmStatusAndDailyLogs(dispatch: AppDispatch): void {
  requestCgmStatus();
  dispatch(getDailyLogsRequest({ startDate: moment().format("YYYY-MM-DD") }));
}

/** CGM display status for badge/UI */
export type CgmStatusBadge = {
  label: string;
  type: "success" | "warning" | "danger" | "info";
} | null;

export interface CgmStatusForBadge {
  displayStatus: string | null;
  step?: string;
  warmupEndDate?: string | null;
}

/**
 * Map CGM Redux state to badge props for UI.
 * Prioritizes warmup: if step is "warmup" and warmupEndDate is in future, show Warmup
 * even when displayStatus might be "CONNECTED" or "bound" (RN can send bound before warmup).
 */
export function getCgmStatusBadge(cgm: CgmStatusForBadge): CgmStatusBadge {
  const { displayStatus, step, warmupEndDate } = cgm;

  // Warmup takes priority — device is in warmup even if RN sent "bound"
  if (step === "warmup" && warmupEndDate) {
    const endTime = new Date(warmupEndDate).getTime();
    if (!Number.isNaN(endTime) && endTime > Date.now()) {
      return { label: "Warmup", type: "warning" };
    }
  }
  if (displayStatus === "WARMUP") {
    return { label: "Warmup", type: "warning" };
  }

  if (!displayStatus) return null;
  switch (displayStatus) {
    case "CONNECTED":
      return { label: "Connected", type: "success" };
    case "DISCONNECTED":
      return { label: "Disconnected", type: "warning" };
    case "TRANSMITTER_DISCONNECT":
      return { label: "Transmitter disconnected", type: "danger" };
    case "BLUETOOTH_OFF":
      return { label: "Bluetooth off", type: "warning" };
    case "TIMEOUT":
      return { label: "Timeout", type: "danger" };
    default:
      return null;
  }
}

/**
 * Map RN `cgmConnectionLive.connectionStatus` or GET /cgm/status `data.status`
 * (same strings as POST /cgm/connection) into UI `CgmDisplayStatus`.
 */
export function cgmApiStatusToDisplayStatus(
  raw: string | null | undefined
): CgmDisplayStatus | null {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim().toUpperCase();

  const aliases: Record<string, CgmDisplayStatus> = {
    CONNECTED: "CONNECTED",
    DISCONNECTED: "DISCONNECTED",
    EXPIRED: "EXPIRED",
    EXIPRED: "EXPIRED",
    TRANSMITTER_DISCONNECT: "TRANSMITTER_DISCONNECT",
    WEAK_SIGNAL: "LOW_POWER",
    ERROR_COMMON: "ERROR",
    BLUETOOTH_OFF: "BLUETOOTH_OFF",
  };

  if (aliases[s]) return aliases[s];
  if (s in CGM_DISPLAY_STATUS_CONFIG) return s as CgmDisplayStatus;
  return null;
}

/**
 * Warmup priority — same rules as `cgmManager.getStatus`: future `warmupEndDate`,
 * `step === "warmup"`, or `displayStatus === "WARMUP"` wins over live/API “connected”.
 */
export function getCgmWarmupPriorityState(cgm: CgmState): {
  active: boolean;
  minutesLeft: number;
} {
  const { step, displayStatus, warmupEndDate } = cgm;
  const warmupEndTime = warmupEndDate ? new Date(warmupEndDate).getTime() : 0;
  const isWarmupActive =
    !Number.isNaN(warmupEndTime) && warmupEndTime > Date.now();
  const active =
    isWarmupActive || step === "warmup" || displayStatus === "WARMUP";
  const remainingMs = isWarmupActive
    ? Math.max(0, warmupEndTime - Date.now())
    : 0;
  const minutesLeft = Math.floor(remainingMs / 60000);
  return { active, minutesLeft };
}

/**
 * Same resolution as `CGMStatusCard`: live RN → API → Redux `displayStatus`, then warmup
 * override to match `cgmManager.getStatus` (RN can report connected while still warming).
 */
export function getCgmStatusCardResolution(cgm: CgmState): {
  rawStatus: string | null;
  resolvedDisplay: CgmDisplayStatus;
  expiredByCycle: boolean;
  sensorStartDate: string | null;
} {
  const api = cgm.statusAndSensorLife.data?.[0] ?? null;
  const rawStatus =
    cgm.cgmConnectionLive?.connectionStatus ?? api?.status ?? null;
  let resolvedDisplay =
    cgmApiStatusToDisplayStatus(rawStatus) ?? cgm.displayStatus;

  const warmup = getCgmWarmupPriorityState(cgm);
  if (warmup.active) {
    resolvedDisplay = "WARMUP";
  }

  const sensorStartDate =
    api?.startDate ?? cgm.cgmConnectionLive?.boundDevice?.bindDate ?? null;
  const expiredByCycle =
    sensorStartDate != null &&
    moment(sensorStartDate).isBefore(
      moment().subtract(CGM_SENSOR_CYCLE_DAYS, "days")
    );

  return {
    rawStatus,
    resolvedDisplay,
    expiredByCycle,
    sensorStartDate,
  };
}

/**
 * Green “connected” Bluetooth vs red/off — matches which branch `CGMStatusCard` would use
 * (default card vs expired / disconnected / Bluetooth off).
 */
export function isCgmStatusCardTransmitterOk(resolution: {
  resolvedDisplay: CgmDisplayStatus;
  rawStatus: string | null;
  expiredByCycle: boolean;
}): boolean {
  const { resolvedDisplay, rawStatus, expiredByCycle } = resolution;
  if (
    resolvedDisplay === "EXPIRED" ||
    rawStatus === "EXPIRED" ||
    rawStatus === "EXIPRED" ||
    expiredByCycle
  ) {
    return false;
  }
  if (resolvedDisplay === "DISCONNECTED") return false;
  if (resolvedDisplay === "TRANSMITTER_DISCONNECT") return false;
  if (resolvedDisplay === "BLUETOOTH_OFF") return false;
  return true;
}

const CGM_DEVICE_LIST_BADGE_TYPES: Partial<
  Record<CgmDisplayStatus, "success" | "warning" | "danger" | "info">
> = {
  CONNECTED: "success",
  WARMUP: "warning",
  BLUETOOTH_OFF: "warning",
  LOW_POWER: "warning",
  DISCONNECTED: "warning",
  TRANSMITTER_DISCONNECT: "danger",
  EXPIRED: "danger",
  TIMEOUT: "danger",
  NOT_CONNECTED: "danger",
  TEMPERATURE_ERROR: "danger",
  ERROR: "danger",
  SENSOR_MISMATCH: "danger",
  SYNCING: "info",
};

export type CgmUnifiedBadgeType = "success" | "warning" | "danger" | "info";

/** Single shape for CGM status chips, badges, and analytics — aligned with `CGMStatusCard`. */
export interface CgmUnifiedUiStatus {
  /** Resolved `CgmDisplayStatus` after expired / disconnected / Bluetooth-off priority. */
  code: CgmDisplayStatus;
  /**
   * Stable machine id: usually same as `code`; `TRANSMITTER_DISCONNECTED` when raw API/RN
   * reports `TRANSMITTER_DISCONNECT`.
   */
  status: string;
  label: string;
  color: string;
  backgroundColor: string;
  type: CgmUnifiedBadgeType;
  /** Unmapped connection string from live snapshot or status API (may be empty). */
  rawConnectionStatus: string | null;
  /** Green Bluetooth / “link OK” — same rules as `isCgmStatusCardTransmitterOk`. */
  transmitterOk: boolean;
}

function cgmMachineStatusId(
  code: CgmDisplayStatus,
  raw: string | null
): string {
  const u = raw?.trim().toUpperCase();
  if (u === "TRANSMITTER_DISCONNECT") {
    return "TRANSMITTER_DISCONNECTED";
  }
  return code;
}

/**
 * Canonical CGM UI status (label, colors, badge type) — same branching as `CGMStatusCard` /
 * device list. Use from `useCgm` or call directly with `CgmState`.
 */
export function getCgmUnifiedUiStatus(cgm: CgmState): CgmUnifiedUiStatus {
  const r = getCgmStatusCardResolution(cgm);
  const transmitterOk = isCgmStatusCardTransmitterOk(r);

  let code: CgmDisplayStatus;
  let label: string;

  if (
    r.resolvedDisplay === "EXPIRED" ||
    r.rawStatus === "EXPIRED" ||
    r.rawStatus === "EXIPRED" ||
    r.expiredByCycle
  ) {
    code = "EXPIRED";
    label = CGM_DISPLAY_STATUS_CONFIG.EXPIRED.label;
  } else if (
    r.resolvedDisplay === "TRANSMITTER_DISCONNECT" ||
    r.rawStatus === "TRANSMITTER_DISCONNECT"
  ) {
    code = "TRANSMITTER_DISCONNECT";
    label = CGM_DISPLAY_STATUS_CONFIG.TRANSMITTER_DISCONNECT.label;
  } else if (r.resolvedDisplay === "DISCONNECTED") {
    code = "DISCONNECTED";
    label = CGM_DISPLAY_STATUS_CONFIG.DISCONNECTED.label;
  } else if (r.resolvedDisplay === "BLUETOOTH_OFF") {
    code = "BLUETOOTH_OFF";
    label = CGM_DISPLAY_STATUS_CONFIG.BLUETOOTH_OFF.label;
  } else {
    code = r.resolvedDisplay;
    if (code === "WARMUP") {
      const w = getCgmWarmupPriorityState(cgm);
      label =
        w.minutesLeft > 0 ? `Warmup (${w.minutesLeft} min left)` : "Warmup";
    } else {
      label = CGM_DISPLAY_STATUS_CONFIG[code].label;
    }
  }

  const cfg = CGM_DISPLAY_STATUS_CONFIG[code];
  const type = CGM_DEVICE_LIST_BADGE_TYPES[code] ?? ("info" as const);

  return {
    code,
    status: cgmMachineStatusId(code, r.rawStatus),
    label,
    color: cfg.color,
    backgroundColor: cfg.bgColor,
    type,
    rawConnectionStatus: r.rawStatus,
    transmitterOk,
  };
}

/**
 * Badge for device list / bottom sheet — derived from `getCgmUnifiedUiStatus`.
 */
export function getCgmDeviceListStatusBadge(cgm: CgmState): {
  label: string;
  type: CgmUnifiedBadgeType;
} {
  const u = getCgmUnifiedUiStatus(cgm);
  return { label: u.label, type: u.type };
}

// ── Readings (ordering / latest by timeInMillis) ─────────────────────────────

/** Sort by SDK epoch ms (UTC). Readings without timeInMillis sort first (0). */
export function sortReadingsByTimeInMillis(
  readings: PocGlucoseJson[]
): PocGlucoseJson[] {
  return [...readings].sort(
    (a, b) => (a.timeInMillis ?? 0) - (b.timeInMillis ?? 0)
  );
}

/** Newest reading by timeInMillis only (not array order). */
export function getLatestReadingByTimeInMillis(
  readings: PocGlucoseJson[]
): PocGlucoseJson | null {
  if (readings.length === 0) return null;
  let best: PocGlucoseJson | null = null;
  let bestT = -Infinity;
  for (const r of readings) {
    const t = r.timeInMillis;
    if (typeof t !== "number" || Number.isNaN(t)) continue;
    if (t >= bestT) {
      bestT = t;
      best = r;
    }
  }
  return best;
}

// ── POST /cgm/logs payload (sort/dedupe by timeInMillis + readingTimestamp) ──

export function readingTimestampIsoFromTimeInMillis(
  timeInMillis: number
): string {
  return new Date(timeInMillis).toISOString();
}

/** Single log row: full SDK shape + authoritative readingTimestamp from timeInMillis. */
export function toCgmLogEntryForApi(
  reading: PocGlucoseJson
): PocGlucoseJsonForApiLog {
  const ms = reading.timeInMillis;
  if (typeof ms !== "number" || Number.isNaN(ms) || ms <= 0) {
    throw new Error(
      "CGM reading requires positive numeric timeInMillis for API log"
    );
  }
  return {
    ...reading,
    readingTimestamp: readingTimestampIsoFromTimeInMillis(ms),
  };
}

/**
 * Sort by timeInMillis, drop invalid rows, dedupe by timeInMillis (last wins), attach ISO timestamp.
 */
export function prepareCgmReadingsForLogsApi(
  readings: PocGlucoseJson[]
): PocGlucoseJsonForApiLog[] {
  const valid = readings.filter(
    (r) =>
      typeof r.timeInMillis === "number" &&
      !Number.isNaN(r.timeInMillis) &&
      r.timeInMillis > 0
  );
  const sorted = sortReadingsByTimeInMillis(valid);
  const byMs = new Map<number, PocGlucoseJsonForApiLog>();
  for (const r of sorted) {
    byMs.set(r.timeInMillis, toCgmLogEntryForApi(r));
  }
  return Array.from(byMs.values()).sort(
    (a, b) => a.timeInMillis - b.timeInMillis
  );
}

// ── GET /cgm/daily_logs query params ─────────────────────────────────────────

/** Optional IANA timezone; set `REACT_APP_CGM_DAILY_LOGS_TIMEZONE` (e.g. Asia/Kolkata) when needed. */
export function withCgmDailyLogsTimezone<T extends { startDate: string }>(
  params: T
): T & { timeZone?: string } {
  const tz = process.env.REACT_APP_CGM_DAILY_LOGS_TIMEZONE;
  if (!tz) return params;
  return { ...params, timeZone: tz };
}
